import type { UiDisplayNutrient } from 'models/display-nutrient';

// The arithmetic behind the nutrient bars that `nutrient-list` draws: which
// target a nutrient is measured against, the text naming that target, and the
// geometry of the bar -- its scale, its tickmark and its four pieces.
//
// It lives apart from the component because none of it needs Angular. Plain
// functions can be tested by importing this file, where the component can only
// be tested by booting it under a Node shim. The component keeps thin methods
// for the ones its template calls, and decides the one thing only it knows:
// which nutrients a card actually draws.
//
// The full model is written up in design-docs/nutrient-bars.md.

export enum NutrientDisplayMode {
  Logged = 'logged',
  Remaining = 'remaining'
}

/**
 * What the bar geometry needs to know about the card a bar sits on.
 *
 * `drawnNutrients` is the set the shared scale factor is measured over: the
 * nutrients whose bars the card actually draws, which is not always every
 * nutrient it was handed. The component decides it -- see
 * `NutrientListComponent.getScaledNutrients()`.
 */
export interface NutrientBarCard {
  displayMode: NutrientDisplayMode;
  drawnNutrients: UiDisplayNutrient[];
}

/**
 * How much wider than its floor a `min` target's bar always is.
 *
 * A floor is not a finish line: hitting "at least 130 g of protein" should not
 * draw a bar that looks finished. So a min bar's full width stands for at least
 * min * 1.5, which parks the target's tickmark two thirds of the way along and
 * leaves the last third as visible headroom.
 *
 * See design-docs/nutrient-bars.md, "the flexible-nutrition buffer".
 */
const MIN_TARGET_BUFFER_FACTOR = 1.5;

/** Which of a nutrient target's three kinds the bar's scale is measured against. */
export enum NutrientTargetKind {
  /** A fixed amount: "about 40 g". */
  Fixed = 'fixed',
  /** A floor: "at least 130 g". */
  Floor = 'floor',
  /** A ceiling: "no more than 30 mg". */
  Ceiling = 'ceiling',
}

/** The target the bar's scale is measured against: which kind it is, and its value. */
export interface NutrientScaleBasis {
  kind: NutrientTargetKind;
  value: number;
}

export interface ProgressBarLayout {
  // Tickmark (optional)
  tickmark: {
    exists: boolean;
    position_pct: number;
  };

  // Primary consumed bar (Consumed mode)
  consumed: {
    exists: boolean;
    left_pct: number;
    right_pct: number;
    roundLeft: boolean;
    roundRight: boolean;
  };

  // Overage bar (Consumed mode, exceeding tickmark)
  overage: {
    exists: boolean;
    left_pct: number;
    right_pct: number;
    roundLeft: boolean;
    roundRight: boolean;
  };

  // Remaining bar (Remaining mode)
  remaining: {
    exists: boolean;
    left_pct: number;
    right_pct: number;
    roundLeft: boolean;
    roundRight: boolean;
  };
}

/**
 * Which of the nutrient's three target kinds the bar's SCALE is measured
 * against, and its value. It looks for a fixed target, then a floor, then a
 * ceiling, and stops at the first key that is PRESENT -- if that key's number
 * is not positive there is no usable scale, and it gives back null rather
 * than moving on to the next kind. Falling through instead would let a
 * nutrient whose floor is zero be scaled against its ceiling, which is a
 * different bar from the one every version of this code has drawn.
 *
 * Floor BEFORE ceiling is the part that matters, and it is not arbitrary. The
 * shape that can tell is a target carrying both a floor and a ceiling and no
 * fixed amount, which is what `actualMealTargets()` produces for a meal's
 * `planningTargets` and which the portion screen renders. For that shape the
 * tickmark comes from the floor, so the value's position on the bar has to
 * come from the floor too, or the overage is drawn against a scale nothing
 * else on the bar uses.
 *
 * This is the ONLY place that decides which target kind applies. Everything
 * else asks -- `getNutrientTargetValue()` for the number,
 * `getNutrientTargetKindSuffix()` for the word, the geometry below for the
 * bar. Until #476 the number was resolved here a second time, in the opposite
 * order, and the two answers disagreed for exactly the shape above: a
 * ceiling printed under a word naming the floor, and a logged bar that
 * stopped short of its own tickmark and left a gap of empty track. Do not
 * reintroduce a second resolution, however local it looks.
 *
 * The guards are written `!(x > 0)` rather than `x <= 0` on purpose:
 * `Number()` yields NaN for a non-numeric target, and NaN fails every
 * comparison, so `x <= 0` would let it through. Tidying the double negative
 * away would reinstate that -- the old code did exactly this and handed back
 * a NaN tickmark.
 */
export function getNutrientScaleBasis(nutrient: UiDisplayNutrient): NutrientScaleBasis | null {
  if (!nutrient.target) return null;

  if (nutrient.target.target !== undefined && nutrient.target.target !== null) {
    const target = Number(nutrient.target.target);
    if (!(target > 0)) return null;
    return { kind: NutrientTargetKind.Fixed, value: target };
  }

  if (nutrient.target.min !== undefined && nutrient.target.min !== null) {
    const min = Number(nutrient.target.min);
    if (!(min > 0)) return null;
    return { kind: NutrientTargetKind.Floor, value: min };
  }

  if (nutrient.target.max !== undefined && nutrient.target.max !== null) {
    const max = Number(nutrient.target.max);
    if (!(max > 0)) return null;
    return { kind: NutrientTargetKind.Ceiling, value: max };
  }

  return null;
}

/**
 * The single number this nutrient is measured against, or null when there is
 * none to measure against.
 *
 * It asks `getNutrientScaleBasis()` and takes that answer, so the number, the
 * word printed beside it and the bar drawn under it all describe the SAME
 * target. Until #476 this method resolved the kinds in its own order --
 * target, then max, then min -- while the bar and the word resolved target,
 * then min, then max. The two disagreed for one shape: a floor and a ceiling
 * with no fixed amount, which is what `actualMealTargets()` produces for a
 * meal's macros and so is the ordinary case rather than a corner one. For
 * that shape this method answered the ceiling while the word beside it named
 * the floor, and the logged bar was measured against the ceiling while its
 * own tickmark came from the floor -- so the bar was drawn with a gap of
 * empty track between the logged part and the part past the target.
 *
 * See design-docs/nutrient-bars.md, "The three target kinds".
 */
export function getNutrientTargetValue(nutrient: UiDisplayNutrient): number | null {
  return getNutrientScaleBasis(nutrient)?.value ?? null;
}

/**
 * The floor and the ceiling of a target that carries BOTH and no fixed
 * amount, or null for every other shape. That shape is what
 * `actualMealTargets()` produces for a meal's macros, so it is the ordinary
 * case rather than a corner one.
 *
 * The floor comes from `getNutrientScaleBasis()` rather than from
 * `nutrient.target.min` directly, so a floor the scale refuses to use -- zero,
 * or not a number -- is not printed as a range the bar is not drawn at.
 */
function getNutrientTargetRange(nutrient: UiDisplayNutrient): { floor: number, ceiling: number } | null {
  const basis = getNutrientScaleBasis(nutrient);
  if (basis === null || basis.kind !== NutrientTargetKind.Floor) {
    return null;
  }

  // if there is no usable ceiling above the floor, this is a plain floor
  const ceiling = Number(nutrient.target?.max);
  if (!(ceiling > 0)) {
    return null;
  }

  return { floor: basis.value, ceiling };
}

/**
 * The number this nutrient's value is printed against: `20-30` for a target
 * with a floor and a ceiling, and the single rounded target for every other
 * shape. Empty when there is no usable target at all.
 *
 * A floor-and-ceiling target prints the RANGE rather than one end of it,
 * because either end alone is a half-truth: a reader shown `20` has not been
 * told about the ceiling, and one shown `30` has not been told about the
 * floor. `formatting.ts` already prints this shape as a range in its own
 * `limitText`, so this is what the rest of the app already shows. It also
 * costs no more width than the word `min` it replaces, which is what lets the
 * portion screen's five columns stay as they are.
 */
export function getNutrientTargetNumberText(nutrient: UiDisplayNutrient): string {
  const range = getNutrientTargetRange(nutrient);
  if (range !== null) {
    return `${Math.round(range.floor)}-${Math.round(range.ceiling)}`;
  }

  const target = getNutrientTargetValue(nutrient);
  return target === null ? '' : `${Math.round(target)}`;
}

/**
 * The `/target` fragment of a value line, or an empty string when there is no
 * usable target to name. It stops at the number because the `linear-card`
 * templates put the unit BETWEEN the number and the kind word, so the caller
 * has to add those two itself.
 *
 * The empty answer matters: a nutrient reaches these templates whenever any
 * of the three target keys is present, and `getNutrientScaleBasis()` gives up
 * on a first key that is not a usable positive number. Printing `/0` there
 * would state a target of zero, which is not what the absence means -- and
 * the bar beside it already draws nothing at all for that nutrient.
 */
export function getNutrientTargetSlashText(nutrient: UiDisplayNutrient): string {
  const numberText = getNutrientTargetNumberText(nutrient);
  return numberText === '' ? '' : `/${numberText}`;
}

/**
 * The word that says what kind of target a number is measured against, with
 * no regard for the display mode.
 *
 * `NutrientListComponent.getNutrientMinMaxSuffix()` suppresses it in Remaining
 * mode. The `compact-macros` card needs it in BOTH modes: with the numbers no
 * longer colour-coded there, this word is the only thing telling "eat 6 g
 * more" apart from "you have 6 g of headroom".
 */
export function getNutrientTargetKindSuffix(nutrient: UiDisplayNutrient): string {
  // a floor with a ceiling above it is printed as a range, which names both
  // ends already -- and no single word is true of it
  if (getNutrientTargetRange(nutrient) !== null) {
    return '';
  }

  const basis = getNutrientScaleBasis(nutrient);
  if (basis === null) return '';

  switch (basis.kind) {
    // a fixed target needs no word: the bare number is the whole story
    case NutrientTargetKind.Fixed:
      return '';
    case NutrientTargetKind.Floor:
      return ' min';
    case NutrientTargetKind.Ceiling:
      return ' max';
  }
}

/**
 * Whether going past this macro's target is good news. True only of a floor
 * that has been cleared without a ceiling also being passed -- clearing "at
 * least 130 g of protein" is the point of having it, whereas going over a
 * limit never is. The card draws a good overage green and every other kind in
 * the macro's own darker shade.
 *
 * The ceiling check is not a corner case. `actualMealTargets()` gives most
 * macros BOTH a floor and a ceiling, so on the portion screen a target
 * usually has one of each; a rule that stopped at the floor would paint an
 * exceeded limit green, which is the app calling bad news good.
 *
 * This is a PER-NUTRIENT rule and is not the `.target-satisfied` class, which
 * is card-level (`match_pc >= 95`) and would recolour every macro including
 * the ones that passed nothing. See design-docs/nutrient-bars.md.
 */
export function isOveragePastFloor(nutrient: UiDisplayNutrient): boolean {
  const basis = getNutrientScaleBasis(nutrient);
  if (basis === null || basis.kind !== NutrientTargetKind.Floor) {
    return false;
  }

  // if there is also a ceiling, clearing the floor stops being good news the
  // moment the value passes it
  const ceiling = Number(nutrient.target?.max);
  if (ceiling > 0) {
    return (Number(nutrient.value) || 0) <= ceiling;
  }

  return true;
}

/**
 * How far along the track the consumed bar reaches, as a percentage of the
 * track's width. It stops at the tickmark: the overage segment draws whatever
 * is past the target.
 */
export function getNutrientProgressPercentage(nutrient: UiDisplayNutrient, card: NutrientBarCard): number {
  // Safely calculate the progress percentage
  const value = Number(nutrient.value) || 0;
  const limit = getNutrientTargetValue(nutrient);

  // If no limit or limit is invalid, return 0
  if (!limit || limit <= 0) {
    return 0;
  }

  // Calculate base percentage (current/limit), capped at the target, then put
  // it on the scale this bar is finally drawn at. Capping first is what stops
  // the consumed bar at the tickmark, which sits at 100 / factor.
  const basePercentage = (value / limit) * 100;
  return Math.min(basePercentage, 100) / getNutrientEffectiveScaleFactor(nutrient, card);
}

// Get the display value based on mode (consumed or remaining)
export function getNutrientDisplayValue(nutrient: UiDisplayNutrient, card: NutrientBarCard): number {
  const value = Number(nutrient.value) || 0;
  if (card.displayMode === NutrientDisplayMode.Remaining) {
    const target = getNutrientTargetValue(nutrient);
    if (target !== null) {
      // deliberately signed: a negative says by how much the target was
      // passed. Flooring it at zero made "300 kcal over" and "exactly on
      // target" both read 0. See design-docs/nutrient-bars.md, rule 3.
      return target - value;
    }
  }
  return value;
}

// Get the progress percentage for remaining mode
export function getNutrientRemainingPercentage(nutrient: UiDisplayNutrient, card: NutrientBarCard): number {
  if (card.displayMode === NutrientDisplayMode.Remaining) {
    const value = Number(nutrient.value) || 0;
    const limit = getNutrientTargetValue(nutrient);

    if (!limit || limit <= 0) {
      return 100; // If no limit, show as fully remaining
    }

    // Calculate remaining percentage
    const remainingPercentage = ((limit - value) / limit) * 100;
    return Math.max(0, Math.min(100, remainingPercentage));
  }
  return getNutrientProgressPercentage(nutrient, card);
}

export function isNutrientOverTarget(nutrient: UiDisplayNutrient): boolean {
  const target = getNutrientTargetValue(nutrient);
  const current = nutrient.value || 0;
  return target > 0 && current > target;
}

export function getNutrientOverageAmount(nutrient: UiDisplayNutrient): number {
  const target = getNutrientTargetValue(nutrient);
  const current = nutrient.value || 0;
  return Math.max(0, current - target);
}

export function getNutrientOveragePercentage(nutrient: UiDisplayNutrient): number {
  const target = getNutrientTargetValue(nutrient);
  const overage = getNutrientOverageAmount(nutrient);
  if (target <= 0) return 0;
  return Math.min(100, (overage / target) * 100);
}

/**
 * How much of the bar's width this ONE nutrient needs, as a multiple of its
 * target. A factor of 1 means the full width stands for the target exactly;
 * 1.55 means it stands for 1.55 targets, which is what pushes the tickmark
 * back to 64.5%.
 *
 * Returns null when this nutrient asks for no rescaling at all: no usable
 * target, nothing logged yet, or still under a fixed target.
 *
 * See design-docs/nutrient-bars.md, rule 1.
 */
function getNutrientOwnScaleFactor(nutrient: UiDisplayNutrient): number | null {
  const basis = getNutrientScaleBasis(nutrient);
  if (basis === null) return null;

  const current = Number(nutrient.value) || 0;
  if (current <= 0) return null;

  const overshoot = getNutrientOvershootFactor(nutrient) ?? 1;

  switch (basis.kind) {
    // a fixed amount draws no line of its own until it is passed: while the
    // value is under it, the end of the bar IS the target
    case NutrientTargetKind.Fixed:
      return current > basis.value ? overshoot : null;
    // a floor always shows its line, parked far enough back to leave headroom
    case NutrientTargetKind.Floor:
      return Math.max(MIN_TARGET_BUFFER_FACTOR, overshoot);
    // a ceiling always shows its line too -- at the very end until passed,
    // because reaching a limit is worth seeing coming
    case NutrientTargetKind.Ceiling:
      return Math.max(1, overshoot);
  }
}

/**
 * How far PAST its target this nutrient has gone, as a multiple of the
 * target -- or null when it has not gone past at all.
 *
 * This is the only thing that widens the whole card (see
 * `getCardScaleFactor()`), and the reason is what "past" means for each kind:
 *
 * - a fixed amount or a ceiling is passed the moment the value exceeds it;
 * - a FLOOR is not. Clearing a floor is the point of having one, so a floor
 *   only counts as overshoot once the value is half again beyond it -- the
 *   same `min * 1.5` the buffer below uses. Treating "past the floor" as
 *   overshoot would rescale every card that merely met its protein target,
 *   which is most of them.
 */
function getNutrientOvershootFactor(nutrient: UiDisplayNutrient): number | null {
  const basis = getNutrientScaleBasis(nutrient);
  if (basis === null) return null;

  const current = Number(nutrient.value) || 0;
  if (current <= 0) return null;

  const limit = basis.kind === NutrientTargetKind.Floor
    ? basis.value * MIN_TARGET_BUFFER_FACTOR
    : basis.value;

  return current > limit ? current / basis.value : null;
}

/**
 * The widening every bar on this card gets AT LEAST: the largest amount by
 * which any nutrient the card draws has gone PAST its target, and never less
 * than 1.
 *
 * It is the floor, not the final word -- `getNutrientEffectiveScaleFactor()`
 * decides what one bar is actually drawn at, and a floor's own buffer can
 * widen its bar further than this.
 *
 * Sharing it is the point. With a factor per bar, a nutrient 5% over and one
 * 80% over both ran their overage to the end of their own track, so the
 * drawing said nothing about which was worse. Sharing one factor makes the
 * longest bar the worst offender. Note it is the largest OVERSHOOT, not the
 * largest factor a nutrient asks for: a floor's 1.5 buffer is something the
 * floor asks for and must not rescale the whole card, which is the bug this
 * distinction exists to prevent. See design-docs/nutrient-bars.md, rule 2.
 */
export function getCardScaleFactor(card: NutrientBarCard): number {
  let factor = 1;
  // for every nutrient this card actually draws
  for (const nutrient of card.drawnNutrients) {
    const overshoot = getNutrientOvershootFactor(nutrient);
    // if it has gone further past its target than anything seen so far
    if (overshoot !== null && overshoot > factor) {
      factor = overshoot;
    }
  }
  return factor;
}

/**
 * The factor ONE bar is finally drawn at: the card's shared factor, or this
 * nutrient's own floor buffer, whichever is wider.
 *
 * The two are different things and only this function decides between them.
 * The card factor comes from whatever has gone furthest past its target and
 * applies to every bar, so they stay comparable. The buffer belongs to a
 * floor alone. When something IS over, the card factor is at least 1.5
 * anyway, so it wins and the buffer never narrows a bar the card widened.
 */
function getNutrientEffectiveScaleFactor(nutrient: UiDisplayNutrient, card: NutrientBarCard): number {
  return Math.max(getCardScaleFactor(card), getNutrientOwnScaleFactor(nutrient) ?? 1);
}

/**
 * Where this nutrient's target falls on its bar, as a percentage of the
 * track's width. Null when no tickmark is drawn at all.
 */
export function getNutrientTickmarkPosition(nutrient: UiDisplayNutrient, card: NutrientBarCard): number | null {
  const factor = getNutrientEffectiveScaleFactor(nutrient, card);

  // if this nutrient asks for no widening of its own, it still needs the
  // target line whenever the card was rescaled around some other nutrient --
  // without it, a bar that shrank would have nothing explaining why
  if (getNutrientOwnScaleFactor(nutrient) === null) {
    if (factor <= 1) return null;
    if (getNutrientTargetValue(nutrient) === null) return null;
  }

  return 100 / factor;
}

/**
 * Whether the bar draws an overage segment: the value has gone past the
 * target the bar is measured against. False when there is no usable target.
 *
 * Every kind compares against the bare target value -- a floor included. That
 * differs on purpose from `getNutrientOvershootFactor()`, which only counts a
 * floor as passed at `min * MIN_TARGET_BUFFER_FACTOR`: that threshold decides
 * whether the whole CARD widens, while this decides whether THIS bar draws the
 * part past its own tickmark, which for a floor starts at the floor itself and
 * grows across the buffer.
 *
 * The target kind comes from `getNutrientScaleBasis()`, never from the target
 * keys directly -- see the rule in its doc block.
 */
export function hasNutrientExceededSegment(nutrient: UiDisplayNutrient): boolean {
  const basis = getNutrientScaleBasis(nutrient);
  if (basis === null) return false;

  const current = Number(nutrient.value) || 0;
  return current > basis.value;
}

// Calculate explicit positioning for all progress bar segments
export function getProgressBarLayout(nutrient: UiDisplayNutrient, card: NutrientBarCard): ProgressBarLayout {
  const current = Number(nutrient.value) || 0;
  const scaleFactor = getNutrientEffectiveScaleFactor(nutrient, card);
  const scaleBasis = getNutrientScaleBasis(nutrient);
  const tickmarkPos = getNutrientTickmarkPosition(nutrient, card);
  const hasTickmark = tickmarkPos !== null;
  // a bar with no tickmark runs the full track, so its "target line" is the end
  const tickPct = tickmarkPos ?? 100;

  // How far the consumed bar reaches (it stops at the tickmark), and where
  // the value itself falls -- both on the scale THIS bar is drawn at, which
  // for a floor inside its buffer is wider than the card's shared one. The
  // value's position is measured against the SAME target the scale came from,
  // so the overage and the tickmark cannot end up on different scales.
  const progressPct = getNutrientProgressPercentage(nutrient, card);
  const valuePct = scaleBasis !== null
    ? Math.min(100, (current / (scaleBasis.value * scaleFactor)) * 100)
    : 0;

  const layout: ProgressBarLayout = {
    tickmark: {
      exists: hasTickmark,
      position_pct: hasTickmark ? tickPct : 0
    },
    consumed: {
      exists: false,
      left_pct: 0,
      right_pct: 0,
      roundLeft: false,
      roundRight: false
    },
    overage: {
      exists: false,
      left_pct: 0,
      right_pct: 0,
      roundLeft: false,
      roundRight: false
    },
    remaining: {
      exists: false,
      left_pct: 0,
      right_pct: 0,
      roundLeft: false,
      roundRight: false
    }
  };

  // A bar that is over its target draws the SAME shape in both modes --
  // consumed up to the tickmark, overage past it. Only the number beside it
  // differs. Before this, Remaining mode collapsed such a bar to zero width,
  // so the screen showed nothing at all exactly where the problem was.
  // See design-docs/nutrient-bars.md, rule 3.
  const isOverTarget = hasTickmark && hasNutrientExceededSegment(nutrient);

  if (card.displayMode === NutrientDisplayMode.Logged || isOverTarget) {
    // Consumed mode: show consumed bar and optionally overage bar
    layout.consumed.exists = true;
    layout.consumed.left_pct = 0;
    layout.consumed.right_pct = progressPct;
    layout.consumed.roundLeft = true;
    layout.consumed.roundRight = !hasTickmark || progressPct < tickPct; // Round if no tickmark or not reaching it

    // Overage bar: the part of the value that sits past the tickmark. Its
    // right edge is just where the value falls on the shared scale, which
    // covers every target kind -- including a minimum still inside its
    // buffer, whose overage grows proportionally across the last third
    // rather than jumping to the end.
    if (isOverTarget) {
      layout.overage.exists = true;
      layout.overage.left_pct = tickPct;
      layout.overage.right_pct = Math.max(tickPct, valuePct);
      layout.overage.roundLeft = false; // Sharp at tickmark
      layout.overage.roundRight = true;
    }
  } else {
    // Remaining mode: show remaining bar from current to tickmark or 100%
    layout.remaining.exists = true;
    layout.remaining.left_pct = progressPct;
    layout.remaining.right_pct = tickPct;
    layout.remaining.roundLeft = true;
    layout.remaining.roundRight = !hasTickmark; // Round if no tickmark (ending at 100%), sharp if ending at tickmark
  }

  return layout;
}
